import React, { useEffect, useMemo, useRef, useState } from "react";
import "./StageEffects.css";

/**
 * Forked lightning emanating from the centre of the screen (where the orb sits).
 * Two flavours: "gold" (default) or "blue". Voice-switchable via set_lightning_color.
 *
 * Each strike picks a random angle, generates a jagged main path + 2-4 random sub-forks,
 * then flashes for ~120ms with a soft afterglow.
 */
export default function StageEffects({ color: colorProp, trigger = 0, targetRect = null }) {
  const [color, setColor] = useState(
    () => colorProp || (typeof window !== "undefined" && localStorage.getItem("rawco_lightning_color")) || "gold"
  );

  useEffect(() => {
    const onStorage = () => {
      const c = localStorage.getItem("rawco_lightning_color") || "gold";
      setColor(c);
    };
    window.addEventListener("rawco-lightning-color", onStorage);
    return () => window.removeEventListener("rawco-lightning-color", onStorage);
  }, []);

  useEffect(() => {
    if (colorProp) setColor(colorProp);
  }, [colorProp]);

  // Strike fires ONLY when `trigger` changes (i.e. a panel just appeared).
  // The seed is re-randomised on each trigger so the bolt is unique.
  const [strikeKey, setStrikeKey] = useState(0);
  const [strikeSeed, setStrikeSeed] = useState(Math.random());
  useEffect(() => {
    if (trigger === 0) return; // don't fire on initial mount
    setStrikeKey(k => k + 1);
    setStrikeSeed(Math.random());
  }, [trigger]);

  // Ambient micro-strikes — fire a tiny bolt near the orb every 5-10s when
  // the strike system is enabled. Adds idle "energy" to the stage.
  const [microKey, setMicroKey] = useState(0);
  const [microSeed, setMicroSeed] = useState(Math.random());
  useEffect(() => {
    const freq = (typeof window !== "undefined" && localStorage.getItem("rawco_lightning_frequency")) || "normal";
    if (freq === "off") return;
    const interval = freq === "frequent" ? 3500 : freq === "rare" ? 12000 : 6500;
    const id = setInterval(() => {
      setMicroKey(k => k + 1);
      setMicroSeed(Math.random());
    }, interval + Math.random() * 1500);
    return () => clearInterval(id);
  }, [color]);

  // Colour palette
  const palette = color === "blue"
    ? { hot: "#e8f5ff", core: "#7fc8ff", glow: "#3a86ff", flash: "rgba(120,200,255,0.07)" }
    : color === "purple"
    ? { hot: "#fbe9ff", core: "#d27bff", glow: "#8a2be2", flash: "rgba(170,90,255,0.06)" }
    : color === "white"
    ? { hot: "#ffffff", core: "#ffffff", glow: "#e0e0ff", flash: "rgba(255,255,255,0.06)" }
    : color === "none"
    ? null
    : { hot: "#fff7d6", core: "#ffd34d", glow: "#ffb800", flash: "rgba(255,184,0,0.05)" }; // gold default

  return (
    <>
      {/* Sky-blue tint that lifts the pure black background a touch */}
      <div className="rc-sky-tint" />

      {palette && strikeKey > 0 && (
        <>
          {/* Soft full-screen flash synced with the strike */}
          <div
            key={`flash-${strikeKey}`}
            className="lightning-flash-once"
            style={{ background: `radial-gradient(circle at center, ${palette.flash} 0%, transparent 65%)` }}
          />
          {/* Forked SVG strike — emanates from screen centre (orb) outward.
              If a panel rect is provided, the bolt is biased TOWARDS it. */}
          <ForkedStrike key={`bolt-${strikeKey}`} seed={strikeSeed} palette={palette} targetRect={targetRect} />
        </>
      )}
      {/* Ambient micro-bolts circling the orb every few seconds */}
      {palette && microKey > 0 && (
        <MicroStrike key={`micro-${microKey}`} seed={microSeed} palette={palette} />
      )}
    </>
  );
}

/* ---------- ForkedStrike: jagged path emanating from screen centre ---------- */
function ForkedStrike({ seed, palette, targetRect = null }) {
  const svgRef = useRef(null);
  const [size, setSize] = useState({ w: typeof window !== "undefined" ? window.innerWidth : 1920, h: typeof window !== "undefined" ? window.innerHeight : 1080 });

  useEffect(() => {
    const onR = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  // Deterministic-ish RNG seeded from `seed`
  const paths = useMemo(() => {
    const rng = mulberry32(Math.floor(seed * 1e9));
    const cx = size.w / 2;
    const cy = size.h / 2;
    const maxLen = Math.max(size.w, size.h) * 0.65;

    const out = [];

    // If a target rect is provided, ALWAYS fire one bolt straight at its centre
    // so the orb visibly "summons" the panel that's just appeared.
    if (targetRect) {
      const tx = targetRect.left + targetRect.width / 2;
      const ty = targetRect.top + targetRect.height / 2;
      const dx = tx - cx;
      const dy = ty - cy;
      const targetAngle = Math.atan2(dy, dx);
      const targetLen = Math.sqrt(dx * dx + dy * dy);
      if (targetLen > 60) {
        out.push(...buildBolt(cx, cy, targetAngle, targetLen, rng, 0));
      }
    }

    // Plus 1-2 random secondary forks for atmosphere
    const forkCount = 1 + Math.floor(rng() * 2);
    for (let f = 0; f < forkCount; f++) {
      const angle = rng() * Math.PI * 2;
      const length = maxLen * (0.55 + rng() * 0.5);
      out.push(...buildBolt(cx, cy, angle, length, rng, 0));
    }
    return out;
  }, [seed, size.w, size.h, targetRect]);

  return (
    <svg
      ref={svgRef}
      className="lightning-strike"
      width={size.w}
      height={size.h}
      viewBox={`0 0 ${size.w} ${size.h}`}
      preserveAspectRatio="none"
      style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 1 }}
      aria-hidden="true"
    >
      <defs>
        <filter id="boltBlur" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="2.2" />
        </filter>
        <filter id="boltSoft" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="10" />
        </filter>
      </defs>
      {/* Outer glow layer */}
      <g filter="url(#boltSoft)" opacity="0.55">
        {paths.map((p, i) => (
          <path key={`g-${i}`} d={p.d} stroke={palette.glow} strokeWidth={p.depth === 0 ? 8 : 4} fill="none" strokeLinecap="round" />
        ))}
      </g>
      {/* Mid layer */}
      <g filter="url(#boltBlur)">
        {paths.map((p, i) => (
          <path key={`m-${i}`} d={p.d} stroke={palette.core} strokeWidth={p.depth === 0 ? 3.5 : 1.8} fill="none" strokeLinecap="round" />
        ))}
      </g>
      {/* Hot core */}
      <g>
        {paths.map((p, i) => (
          <path key={`c-${i}`} d={p.d} stroke={palette.hot} strokeWidth={p.depth === 0 ? 1.4 : 0.8} fill="none" strokeLinecap="round" opacity={p.depth === 0 ? 1 : 0.85} />
        ))}
      </g>
    </svg>
  );
}

/* ---------- Helpers ---------- */
function mulberry32(a) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Recursive lightning generator. Returns array of {d, depth} path objects.
 *  - depth 0: main trunk
 *  - depth 1+: sub-forks
 */
function buildBolt(x0, y0, angle, length, rng, depth) {
  // Segments along the main path — jaggier for trunks
  const segs = depth === 0 ? 24 : 10 + Math.floor(rng() * 8);
  const step = length / segs;
  // perpendicular jitter scale — bigger near trunk start, narrows toward tip
  const jitter = depth === 0 ? step * 0.55 : step * 0.4;

  let x = x0, y = y0;
  const pts = [[x, y]];
  const forks = [];

  for (let i = 1; i <= segs; i++) {
    const t = i / segs;
    // Slight drift in angle so the bolt curves naturally
    const angleDrift = (rng() - 0.5) * 0.35 * (1 - t * 0.5);
    const a = angle + angleDrift;
    // Forward step
    const fx = Math.cos(a) * step;
    const fy = Math.sin(a) * step;
    // Perpendicular jitter
    const perp = a + Math.PI / 2;
    const j = (rng() - 0.5) * jitter * (1 - t * 0.3);
    x += fx + Math.cos(perp) * j;
    y += fy + Math.sin(perp) * j;
    pts.push([x, y]);

    // Occasionally spawn a sub-fork (only from trunk + first level)
    if (depth < 2 && i > segs * 0.15 && i < segs * 0.85 && rng() < (depth === 0 ? 0.18 : 0.10)) {
      const branchAngle = a + (rng() < 0.5 ? -1 : 1) * (0.4 + rng() * 0.8);
      const branchLen = (length - i * step) * (0.3 + rng() * 0.45);
      forks.push(...buildBolt(x, y, branchAngle, branchLen, rng, depth + 1));
    }
  }

  const d = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  return [{ d, depth }, ...forks];
}

/* ---------- MicroStrike: tiny ambient bolts near the orb ---------- */
function MicroStrike({ seed, palette }) {
  const [size] = useState(() => ({ w: typeof window !== "undefined" ? window.innerWidth : 1920, h: typeof window !== "undefined" ? window.innerHeight : 1080 }));
  const rng = mulberry32(Math.floor(seed * 1e9));
  const cx = size.w / 2;
  const cy = size.h / 2;
  // Position the micro-bolt on a random angle around the orb, ~180-280px out
  const orbRadius = Math.min(size.w, size.h) * 0.18;
  const startRadius = orbRadius + 20 + rng() * 40;
  const angle = rng() * Math.PI * 2;
  const x0 = cx + Math.cos(angle) * startRadius;
  const y0 = cy + Math.sin(angle) * startRadius;
  // Bolt goes outward, length 60-140px
  const length = 60 + rng() * 80;
  const bolt = buildBolt(x0, y0, angle, length, rng, 1); // depth 1 → thinner
  return (
    <svg
      width={size.w}
      height={size.h}
      viewBox={`0 0 ${size.w} ${size.h}`}
      style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 1 }}
      className="lightning-micro"
      aria-hidden="true"
    >
      <g style={{ filter: `drop-shadow(0 0 4px ${palette.glow})` }}>
        {bolt.map((p, i) => (
          <path key={i} d={p.d} stroke={palette.hot} strokeWidth={1.2} fill="none" strokeLinecap="round" opacity={0.85} />
        ))}
      </g>
    </svg>
  );
}
